import { Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import SideBar from '../sidebar';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { LoginType } from 'api-types/models/LoginDto';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectAuthUser } from 'app/authSlice';
import useAmplifyAuth from 'hooks/useAmplifyAuth';
import IRoute from 'interfaces/route';
import { getRoutesBasedOnUserGroups } from 'utils/getRoutesBasedOnUserGroups';
import { selectUserSettings } from 'app/userSlice';
import MenuItemsLoader from 'components/loading/menuLoader';
import { loginRequest } from 'authConfig';
import { renewSSOTokens } from 'utils/renewSSOTokens';
import { setConfigThunk } from 'app/configSlice';

interface IProps {
    route: IRoute;
}

export function ProtectedRoute({ route }: Readonly<IProps>): React.ReactNode {
    const loginType = localStorage.getItem('login');
    const { instance, accounts, inProgress } = useMsal();
    const dispatch = useAppDispatch();

    useAmplifyAuth();

    const user = useAppSelector(selectAuthUser);
    const isAuthenticated = useIsAuthenticated();
    const userSettings = useAppSelector(selectUserSettings);

    // Renew expired SSO tokens via useEffect (not in render body)
    useEffect(() => {
        if (
            accounts.length &&
            accounts[0].idTokenClaims?.exp &&
            accounts[0].idTokenClaims.exp < Date.now() / 1000 &&
            inProgress === InteractionStatus.None
        ) {
            renewSSOTokens(instance, accounts[0], loginRequest).then(
                (token) => {
                    if (token) {
                        dispatch(
                            setConfigThunk({
                                idToken: token.idToken,
                                accessToken: token.accessToken
                            })
                        );
                    }
                }
            );
        }
    }, [accounts, instance, inProgress, dispatch]);

    // Wait for MSAL to finish processing (e.g. redirect response) before
    // making any authentication decisions. Without this, useIsAuthenticated()
    // may return false while MSAL is still handling the redirect, causing
    // an incorrect redirect back to /login.
    if (inProgress !== InteractionStatus.None) {
        return <MenuItemsLoader />;
    }

    if (
        (loginType === LoginType.SPORTRADAR && !isAuthenticated) ||
        (loginType === LoginType.BRUNCH && user === null) ||
        loginType === null ||
        (loginType !== LoginType.BRUNCH && route.brunchOnly === true)
    ) {
        return <Navigate to='/login' />;
    }

    if (loginType !== LoginType.SPORTRADAR && !user) {
        return <MenuItemsLoader />;
    }

    const routes = getRoutesBasedOnUserGroups(user);

    if (
        !routes.includes(route.path) &&
        !(route.path === '/users' && userSettings?.officeManagement)
    ) {
        return <Navigate to={routes[0]} />;
    }

    return (
        <div className='app-content'>
            {route.sidebar !== false && <SideBar />}
            <route.component name={route.name} {...route.props} />
        </div>
    );
}
